import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../../basics/interface/CustomModal"
import SelectV2 from "../../basics/input/SelectV2"
import InputV2 from "../../basics/input/InputV2"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import {
	DEP_STATE_CANCELED,
	DEP_STATE_DISPATCHED,
	DEP_STATE_EN_ATTENTE,
	DEP_STATE_EN_SUSPENS,
	DEP_STATE_READY_TO_DISPATCH,
	DEP_STATE_RENDEZ_VOUS,
} from "../../../utilities/Addons"
import {
	corpsDelta,
	putDepannageDispatch,
	putDepannageDispatchReset,
} from "../../../store/actions/depannageDispatchActions"
import { filterEmployesGroupe } from "../../../utilities/employes"
import { OuvrirFicheBtn, RelivraisonBadge } from "./DepMissionCardV2"

// Modal « Traiter la mission » (V2) : dispatcher maintenant à un dépanneur,
// planifier (ce matin / cet après-midi / créneau libre), suspendre ou mettre
// en attente. `onlyDispatch` (Planning) : seulement l'assignation immédiate.
const DepDispatchDispatch = ({
	visible,
	closeModal,
	dm,
	onlyDispatch = false,
	onModifier = null,
}) => {
	const dispatch = useDispatch()
	const [selectedDepanneur, setSelectedDepanneur] = useState(0)
	const [btnClicked, setBtnClicked] = useState("")
	// Convention modal : pas de second modal, switch de confirmation sur le
	// bouton (« Annuler la mission » → « Confirmer l'annulation ? »).
	const [confirmAnnulation, setConfirmAnnulation] = useState(false)
	// B3 (CDC 16.10) : retour à « Prêt au dispatch » (switch de confirmation).
	const [confirmPret, setConfirmPret] = useState(false)
	// Créneau libre (planification à une date/heure précises).
	const [creneauDate, setCreneauDate] = useState(
		moment().format("YYYY-MM-DD"),
	)
	const [creneauHeure, setCreneauHeure] = useState("08:00")
	const [creneauDuree, setCreneauDuree] = useState(90)
	const { employes: depanneurs } = useSelector((state) => state.masterReducer)
	const { putDepsToDispatch } = useSelector(
		(state) => state.depannageDispatchInfo,
	)
	const busy = putDepsToDispatch.saving || putDepsToDispatch.saved

	// Défensif : purge tout état saved/error laissé par un autre flux (drop ou
	// resize du planning) — sinon le modal se croit « sauvegardé » à
	// l'ouverture et se ferme tout seul.
	useEffect(() => {
		dispatch(putDepannageDispatchReset())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const put = (key, changes) => {
		if (busy) return
		setBtnClicked(key)
		dispatch(putDepannageDispatch(corpsDelta(dm, changes)))
	}

	const dispatcher = () =>
		put("dispatcher", {
			etat: DEP_STATE_DISPATCHED,
			depanneur: selectedDepanneur,
			date_rendez_vous: null,
			delta_rendez_vous: 0,
		})
	// A15 (CDC 16.10) : un créneau du jour déjà passé n'est plus proposé (un
	// rendez-vous passé disparaît du planning).
	const maintenant = moment()
	const matinPossible = maintenant.isBefore(moment("07:30", "HH:mm"))
	const apremPossible = maintenant.isBefore(moment("13:00", "HH:mm"))

	const ceMatin = () =>
		put("ceMatin", {
			etat: DEP_STATE_RENDEZ_VOUS,
			date_rendez_vous: `${moment().format("DD-MM-YYYY")} 07:30`,
			delta_rendez_vous: 300,
		})
	const cetAprem = () =>
		put("cetAprem", {
			etat: DEP_STATE_RENDEZ_VOUS,
			date_rendez_vous: `${moment().format("DD-MM-YYYY")} 13:00`,
			delta_rendez_vous: 240,
		})
	const creneau = () =>
		put("creneau", {
			etat: DEP_STATE_RENDEZ_VOUS,
			date_rendez_vous: `${moment(creneauDate, "YYYY-MM-DD").format("DD-MM-YYYY")} ${creneauHeure}`,
			delta_rendez_vous: Number(creneauDuree) || 90,
		})
	const suspendre = () =>
		put("suspendre", {
			etat: DEP_STATE_EN_SUSPENS,
			date_rendez_vous: null,
			delta_rendez_vous: 0,
		})
	const enAttente = () =>
		put("enAttente", {
			etat: DEP_STATE_EN_ATTENTE,
			date_rendez_vous: null,
			delta_rendez_vous: 0,
		})
	// B3 : mission planifiée / suspendue / en attente → « Prêt au dispatch »,
	// sans date ni durée.
	const planifiee = [
		DEP_STATE_EN_SUSPENS,
		DEP_STATE_EN_ATTENTE,
		DEP_STATE_RENDEZ_VOUS,
	].includes(dm.etat)
	const remettreEnPret = () => {
		if (!confirmPret) {
			setConfirmPret(true)
			return
		}
		put("pret", {
			etat: DEP_STATE_READY_TO_DISPATCH,
			date_rendez_vous: null,
			delta_rendez_vous: 0,
		})
	}
	// Fonction : appelée au rendu, après la définition d'ActionBtn.
	const actionsPlanifiee = () =>
		planifiee ? (
			<>
				{onModifier && (
					<ActionBtn
						id="modifier"
						label="Modifier la mission"
						onClick={() => onModifier(dm)}
					/>
				)}
				<ActionBtn
					id="pret"
					label={
						confirmPret
							? "Confirmer le retour à Prêt ?"
							: "Remettre en Prêt"
					}
					onClick={remettreEnPret}
				/>
			</>
		) : null
	// Annulation : le backend notifie le dépanneur si la mission était entre
	// ses mains et nettoie le dispatch (→ onglet Annulées).
	const annuler = () => {
		if (!confirmAnnulation) {
			setConfirmAnnulation(true)
			return
		}
		put("annuler", { etat: DEP_STATE_CANCELED })
	}

	// ✓ 1 s puis fermeture (reset du slice partagé avec les autres onglets).
	useEffect(() => {
		if (!putDepsToDispatch.saved) return
		const timeout = setTimeout(() => {
			dispatch(putDepannageDispatchReset())
			closeModal()
		}, 1000)
		return () => clearTimeout(timeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [putDepsToDispatch.saved])

	// Bouton d'action avec spinner→✓ ciblé sur le bouton cliqué.
	const ActionBtn = ({
		id,
		label,
		onClick,
		disabled = false,
		primary = false,
		danger = false,
	}) => (
		<button
			type="button"
			style={{
				...(primary ? gs.btnPrimary : gs.btnSecondary),
				minWidth: 130,
				...(danger
					? {
							color: COLORS.danger,
							borderColor: COLORS.danger,
						}
					: {}),
			}}
			onClick={onClick}
			disabled={disabled || busy}
		>
			{btnClicked === id && putDepsToDispatch.saving ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : btnClicked === id && putDepsToDispatch.saved ? (
				<FontAwesomeIcon icon={faCheck} />
			) : (
				label
			)}
		</button>
	)

	if (!visible) return null

	return (
		<CustomModal
			title="Traiter la mission"
			subtitle={`${dm.marque} ${dm.modele} ${dm.plaques ? `— ${dm.plaques}` : ""}`.trim()}
			onClose={busy ? () => {} : closeModal}
			showCloseButton={!busy}
			buttons={[
				{
					title: "Fermer",
					styleName: "secondary",
					onClick: closeModal,
					disabled: busy,
				},
			]}
		>
			{/* Mission liée à une fiche : seul repère du Planning (ses
			    pastilles de créneau n'ont pas la place d'un badge). */}
			{dm.dep ? (
				<div style={st.ficheLiee}>
					<RelivraisonBadge dep={dm} />
					<OuvrirFicheBtn ficheId={dm.dep} />
				</div>
			) : null}
			<div style={st.section}>
				<div style={st.sectionTitle}>Dispatcher maintenant</div>
				<div style={st.row}>
					<SelectV2
						value={selectedDepanneur}
						onChange={(e) =>
							setSelectedDepanneur(parseInt(e.target.value))
						}
						style={st.input}
					>
						<option value={0}>Choisir un dépanneur…</option>
						{filterEmployesGroupe(
							depanneurs,
							selectedDepanneur,
						).map((d) => (
							<option key={`depanneur_${d.id}`} value={d.id}>
								{d.nom}
							</option>
						))}
					</SelectV2>
					<ActionBtn
						id="dispatcher"
						label="Dispatcher"
						onClick={dispatcher}
						disabled={selectedDepanneur === 0}
						primary
					/>
				</div>
			</div>

			{!onlyDispatch && (
				<>
					{(matinPossible || apremPossible) && (
						<div style={st.section}>
							<div style={st.sectionTitle}>
								Planifier aujourd'hui
							</div>
							<div style={st.row}>
								{matinPossible && (
									<ActionBtn
										id="ceMatin"
										label="Ce matin (07:30)"
										onClick={ceMatin}
									/>
								)}
								{apremPossible && (
									<ActionBtn
										id="cetAprem"
										label="Cet après-midi (13:00)"
										onClick={cetAprem}
									/>
								)}
							</div>
						</div>
					)}

					<div style={st.section}>
						<div style={st.sectionTitle}>
							Planifier ultérieurement
						</div>
						<div style={st.rowNoWrap}>
							<InputV2
								type="date"
								value={creneauDate}
								onChange={(e) => setCreneauDate(e.target.value)}
								style={{ ...st.input, flex: "0 0 140px" }}
							/>
							<InputV2
								type="time"
								value={creneauHeure}
								onChange={(e) =>
									setCreneauHeure(e.target.value)
								}
								style={{ ...st.input, flex: "0 0 96px" }}
							/>
							<SelectV2
								value={creneauDuree}
								onChange={(e) =>
									setCreneauDuree(e.target.value)
								}
								style={{ ...st.input, flex: "0 0 96px" }}
							>
								<option value={30}>30 min</option>
								<option value={60}>1 h</option>
								<option value={90}>1 h 30</option>
								<option value={120}>2 h</option>
								<option value={180}>3 h</option>
								<option value={240}>4 h</option>
							</SelectV2>
							<ActionBtn
								id="creneau"
								label="Planifier"
								onClick={creneau}
								disabled={!creneauDate || !creneauHeure}
							/>
						</div>
					</div>

					<div style={st.section}>
						<div style={st.sectionTitle}>Autres</div>
						<div style={st.row}>
							{actionsPlanifiee()}
							{dm.etat !== DEP_STATE_EN_SUSPENS && (
								<ActionBtn
									id="suspendre"
									label="Suspendre"
									onClick={suspendre}
								/>
							)}
							{dm.etat !== DEP_STATE_EN_ATTENTE && (
								<ActionBtn
									id="enAttente"
									label="Mettre en attente"
									onClick={enAttente}
								/>
							)}
							<ActionBtn
								id="annuler"
								label={
									confirmAnnulation
										? "Confirmer l'annulation ?"
										: "Annuler la mission"
								}
								onClick={annuler}
								danger
							/>
						</div>
					</div>
				</>
			)}
			{onlyDispatch && (
				<div style={st.section}>
					<div style={st.sectionTitle}>Autres</div>
					<div style={st.row}>
						{actionsPlanifiee()}
						{dm.etat !== DEP_STATE_EN_ATTENTE && (
							<ActionBtn
								id="enAttente"
								label="Mettre en attente"
								onClick={enAttente}
							/>
						)}
						<ActionBtn
							id="annuler"
							label={
								confirmAnnulation
									? "Confirmer l'annulation ?"
									: "Annuler la mission"
							}
							onClick={annuler}
							danger
						/>
					</div>
				</div>
			)}
			{putDepsToDispatch.error && (
				<div style={st.error}>
					{putDepsToDispatch.error.httpStatus === 409
						? `${putDepsToDispatch.error.message} ${Object.values(
								putDepsToDispatch.error.fields || {},
							)
								.map((c) => c.message)
								.join(" ")} Fermez et rouvrez la mission.`
						: "Le changement d'état a échoué. Réessayez."}
				</div>
			)}
		</CustomModal>
	)
}

const st = {
	section: { marginBottom: 16 },
	ficheLiee: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 8,
		marginBottom: 16,
	},
	sectionTitle: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		marginBottom: 8,
	},
	row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
	// Créneau libre : date + heure + durée + bouton sur UNE ligne.
	rowNoWrap: {
		display: "flex",
		gap: 10,
		alignItems: "center",
		flexWrap: "nowrap",
	},
	input: {
		flex: 1,
		height: 38,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
	error: { fontSize: 13, color: COLORS.danger },
}

export default DepDispatchDispatch
